import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  HTMLAttributes,
  ReactNode,
  Ref,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import useHighlightColor from '../../hooks/useHighlightColor';
import useImagePreloader from '../../hooks/useImagePreloader';
import useOutsideClickEffect from '../../hooks/useOutsideClickEffect';
import { BORDER_RADIUS, BORDER_WIDTH } from '../../styles/borders';
import { MEDIA_QUERY } from '../../styles/breakpoints';
import { COLOR, GREYSCALE, PirateShipColor } from '../../styles/colors';
import { SPACING } from '../../styles/spacing';
import { TYPOGRAPHY } from '../../styles/typography';
import Icon from '../Icon';

export type DropdownSelectOption<V extends string | number, D = unknown> = {
  value: V;
  title?: ReactNode;
  description?: ReactNode;
  iconUrl?: string;
  disabled?: boolean;
  data?: D; // if it makes sense to bind extra data to the option, do it here
};

export type HighlightProps = {
  disabled?: boolean;
  open: boolean;
  highlightColor?: PirateShipColor;
};

export type DropdownOptionProps = {
  open: boolean;
  disabled: boolean;
  isSingleOption?: boolean;
};

export type CaretSize = 'lg' | 'xl';

export type DropdownSelectProps<
  V extends string | number,
  O extends DropdownSelectOption<V>, // this can be defined with a tighter type depending on use-case
> = Omit<
  HTMLAttributes<HTMLDivElement>,
  'type' | 'role' | 'aria-selected' | 'onChange' | 'onClick' | 'value' | 'defaultValue'
> & {
  name: string;
  options: O[];
  datadogAction?: string;
  autofilled?: boolean;
  error?: boolean;
  value?: NoInfer<V>;
  defaultValue?: NoInfer<V>;
  disabled?: boolean;
  onChange?: (value: V, event: { stopPropagation: () => void }) => void;
  onRenderOption?: (option: O) => ReactNode;
  onRenderOpener?: () => ReactNode;
  imageSize?: number[]; // [smallScreenSize, largeScreenSize]
  labelledById?: string; // for accessibility, to link the dropdown with its label
  caretSize?: CaretSize;
  isPii?: boolean; // For GTM tracking PII redaction
};

export const StyledDropdownSelectTitle = styled.div`
  font-size: ${TYPOGRAPHY.fontSize.md};
  color: ${GREYSCALE.black};
  font-weight: ${TYPOGRAPHY.fontWeight.regular};
  line-height: ${TYPOGRAPHY.fontSize.lg};
  margin: ${SPACING.none};
`;

export const StyledDropdownDescription = styled.small`
  font-size: ${TYPOGRAPHY.fontSize.sm};
  line-height: ${TYPOGRAPHY.fontSize.md};
  color: ${GREYSCALE.grey50};
  padding-top: ${SPACING.sm};

  & strong {
    color: ${GREYSCALE.black};
  }
`;

export const StyledDropdownIconWrapper = styled.div<{ size: CaretSize; align: 'right' | 'left' }>`
  box-sizing: border-box;
  ${({ align, size }) => css`
    padding-${align === 'right' ? 'left' : 'right'}: ${size === 'lg' ? '12px' : '15px'};
    padding-${align === 'right' ? 'right' : 'left'}: ${size === 'lg' ? '5px' : '6px'};
  `}
  display: flex;
  align-self: center;
  justify-content: flex-end;
`;
export const StyledDropdownOption = styled.div<DropdownOptionProps>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: space-around;
  list-style: none;
  appearance: none;
  background: none;
  border: 0;
  text-align: inherit;
  padding: ${SPACING.md};
  color: ${GREYSCALE.grey80};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  :hover,
  &[data-active='true'] {
    background-color: ${COLOR.lightBlue};
  }
  &[hidden] {
    display: none;
  }
  cursor: ${({ isSingleOption, disabled }) => {
    if (disabled) {
      return 'not-allowed';
    }
    if (isSingleOption) {
      return 'auto';
    }
    return 'pointer';
  }};
  border-bottom: ${BORDER_WIDTH.sm} solid ${GREYSCALE.grey30};
  border-spacing: ${SPACING.none};
  ${({ isSingleOption, open }) =>
    !isSingleOption &&
    open &&
    css`
      border-bottom-color: ${GREYSCALE.grey30};
      &:hover,
      &[data-active='true'] {
        background: ${COLOR.lightBlue};
        border-color: ${COLOR.blue};
        border-top: ${BORDER_WIDTH.sm} solid ${COLOR.blue};
        margin-top: -2px;
      }
    `};

  &:last-child {
    border-bottom: ${BORDER_WIDTH.none};
  }

  &:first-of-type {
    border-radius: ${BORDER_WIDTH.lg} ${BORDER_WIDTH.lg} ${BORDER_WIDTH.none} ${BORDER_WIDTH.none};
  }

  ${({ open }) =>
    open &&
    css`
      background-color: ${GREYSCALE.white};
    `}
`;
export const StyledOptionsWrapper = styled.div`
  list-style: none;
  padding: ${SPACING.none};
  margin: ${SPACING.none};
  font-size: ${TYPOGRAPHY.fontSize.md};
  line-height: ${TYPOGRAPHY.fontSize.md};
  min-height: ${TYPOGRAPHY.fontSize.md};
  position: relative;
`;
export const StyledCheckboxWrapper = styled.div`
  padding-right: ${SPACING.sm};
`;

export const StyledDropdownSelectWrapper = styled.div<HighlightProps & { autofilled?: boolean }>`
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  list-style: none;
  border-style: solid;
  border-width: ${BORDER_WIDTH.sm};
  border-color: ${({ highlightColor, autofilled }) =>
    autofilled ? COLOR.blue : highlightColor || GREYSCALE.grey30};
  border-radius: ${BORDER_RADIUS.sm};
  outline-color: ${COLOR.blue};
  background: ${({ highlightColor, autofilled, open }) => {
    if (highlightColor === COLOR.blue || open) {
      return GREYSCALE.white;
    }
    if (autofilled) {
      return `linear-gradient(to bottom, ${GREYSCALE.white} 24%, ${COLOR.lightBlue} 100%)`;
    }
    return `linear-gradient(to bottom, ${GREYSCALE.white} 24%, ${GREYSCALE.grey20} 100%)`;
  }};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  position: relative;
`;
export const StyledDropdownImage = styled.img<{ imageSize?: number[] }>`
  max-width: 130px;
  max-height: 130px;
  margin-right: 20px;
  ${({ imageSize }) =>
    imageSize?.length === 2 &&
    css`
      width: ${imageSize[0]}px;
      height: ${imageSize[0]}px;

      @media (min-width: ${MEDIA_QUERY.smMin}) {
        width: ${imageSize[1]}px;
        height: ${imageSize[1]}px;
      }
    `}
`;
export const StyledTitleWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-self: center;

  &:hover {
    .Icon {
      color: ${GREYSCALE.grey30};
    }
  }
`;

let searchString = '';
let searchTimeout: number = 0;

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
function filterOptions(options: string[] = [], filter: string, exclude: string[] = []) {
  return options.filter((option) => {
    const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
    return matches && exclude.indexOf(option) < 0;
  });
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
function getIndexByLetter(options: string[], filter: string, startIndex: number = 0) {
  const firstMatch = filterOptions(options, filter)[0];

  // first check if there is an exact match for the typed string
  if (firstMatch) {
    return options.indexOf(firstMatch);
  }

  const orderedOptions = [...options.slice(startIndex), ...options.slice(0, startIndex)];
  const allSameLetter = (letters: string[]) => letters.every((letter) => letter === letters[0]);

  // if the same letter is being repeated, cycle through first-letter matches
  if (allSameLetter(filter.split(''))) {
    const matches = filterOptions(orderedOptions, filter[0]);
    return options.indexOf(matches[0]);
  }

  // if no matches, return -1
  return -1;
}

function getSearchString(letter: string) {
  // reset typing timeout and start new timeout
  // this allows us to make multiple-letter matches, like a native select
  if (typeof searchTimeout === 'number') {
    window.clearTimeout(searchTimeout);
    searchTimeout = 0;
  }

  searchTimeout = window.setTimeout(() => {
    searchString = '';
    searchTimeout = 0;
  }, 500);

  // add most recent letter to saved search string
  searchString += letter;

  return searchString;
}

function DropdownSelectComponent<V extends string | number, O extends DropdownSelectOption<V>>(
  props: DropdownSelectProps<V, O>,
  ref: Ref<HTMLSelectElement>,
) {
  const {
    id,
    name,
    className,
    options,
    value,
    defaultValue,
    disabled: selectDisabled,
    onChange,
    onRenderOption,
    imageSize,
    labelledById,
    error,
    autofilled,
    datadogAction,
    caretSize = 'lg',
    isPii,
    ...others
  } = props;
  const listboxId = useId();

  const preloadImage = useImagePreloader();

  const [selectedValue, setSelectedValue] = useState(value ?? defaultValue);
  const [activeOptionIndex, setActiveOptionIndex] = useState<number>();

  // map the current value to its option
  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue),
    [options, selectedValue],
  );
  const [open, setOpen] = useState(false);
  const isSingleOption = options.length === 1;

  // sort options so that selected option is first item
  const sortedOptions = useMemo(
    () =>
      [selectedOption, ...options.filter((option) => option !== selectedOption)].filter(Boolean),
    [options, selectedOption],
  );

  // Preload all icons when options change (usually just after mount)
  useEffect(() => {
    options.flatMap((option) => (option.iconUrl ? [option.iconUrl] : [])).forEach(preloadImage);
  }, [options, preloadImage]);

  // Close dropdown menu on outside click
  const openDropdownMenuRef = useRef<HTMLDivElement>(null);
  useOutsideClickEffect([openDropdownMenuRef], () => setOpen(false));

  // Reset active option index when dropdown is closed
  useEffect(() => {
    if (!open) {
      setActiveOptionIndex(undefined);
    }
  }, [open]);

  // Scroll active option into view
  useEffect(() => {
    if (activeOptionIndex !== undefined) {
      const activeOption = openDropdownMenuRef.current?.querySelector('[data-active="true"]');
      activeOption?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeOptionIndex]);

  // React to value changes
  useEffect(() => {
    setSelectedValue(value ?? defaultValue);
  }, [value, defaultValue, options]);

  const renderOptionContent = (option: O | undefined) => {
    const { title = '', description, iconUrl } = option ?? {};

    if (onRenderOption && option) {
      return onRenderOption(option);
    }

    return (
      <>
        {iconUrl && <StyledDropdownImage src={iconUrl} imageSize={imageSize} aria-hidden />}
        <StyledTitleWrapper>
          <StyledDropdownSelectTitle>{title}</StyledDropdownSelectTitle>
          {description && <StyledDropdownDescription>{description}</StyledDropdownDescription>}
        </StyledTitleWrapper>
      </>
    );
  };
  const renderCaret = (size: CaretSize) => {
    return (
      <StyledDropdownIconWrapper size={size} align="right" aria-hidden>
        <Icon icon={open ? 'caret-up' : 'caret-down'} size={size} />
      </StyledDropdownIconWrapper>
    );
  };
  const renderStyledDropdownOpener = (option: O | undefined, caretSize: CaretSize) => {
    const { disabled: optionDisabled = false } = option ?? {};

    return (
      <StyledDropdownOption
        isSingleOption={isSingleOption}
        open={open}
        hidden={open} // Hide when open in favor of listbox option
        disabled={selectDisabled || optionDisabled}
        aria-live={!open ? 'polite' : 'off'} // Announce changes made in closed state by typing
        onClick={(event) => {
          if (isSingleOption) {
            event.stopPropagation();
            return;
          }

          setOpen(!selectDisabled && !open);
        }}
        {...others}
      >
        {renderOptionContent(option)}
        {!isSingleOption && renderCaret(caretSize)}
      </StyledDropdownOption>
    );
  };
  const renderStyledDropdownOption = (option: O, index: number, caretSize: CaretSize) => {
    const isSelected = option === selectedOption;
    const { value: optionValue, disabled: optionDisabled = false } = option ?? {};

    return (
      <StyledDropdownOption
        key={optionValue}
        id={listboxId + index}
        role="option"
        aria-selected={isSelected}
        data-active={index === activeOptionIndex}
        data-testid={`dropdown-${name}-${optionValue}`}
        data-option-value={optionValue} // required in acceptance tests for the findability of the button representing a select option
        isSingleOption={isSingleOption}
        open={open}
        disabled={selectDisabled || optionDisabled}
        onClick={(event) => {
          if (optionDisabled) {
            return;
          }

          // uncontrolled DropdownSelect
          if (value === undefined) {
            setSelectedValue(optionValue);
          }

          onChange?.(optionValue, event);
        }}
        {...others}
      >
        {renderOptionContent(option)}
        {!isSingleOption && index === 0 && renderCaret(caretSize)}
      </StyledDropdownOption>
    );
  };

  return (
    <>
      <select
        hidden
        aria-hidden
        ref={ref}
        name={name}
        value={selectedValue}
        disabled={selectDisabled}
        data-is-pii={Boolean(isPii)}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => {
          // Retrieving from the list of options preserves correct type
          const option = sortedOptions.at(event.target.options.selectedIndex);
          if (option && onChange) {
            onChange(option.value, event);
          }
        }}
      >
        {sortedOptions.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.title || option.value}
          </option>
        ))}
      </select>
      <StyledDropdownSelectWrapper
        id={id}
        data-name={name}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-labelledby={labelledById}
        aria-activedescendant={activeOptionIndex !== undefined ? listboxId + activeOptionIndex : ''}
        data-dd-action-name={datadogAction}
        tabIndex={0}
        ref={open ? openDropdownMenuRef : null}
        highlightColor={useHighlightColor(Boolean(error), open)}
        autofilled={autofilled}
        disabled={selectDisabled}
        className={className}
        open={open}
        onClick={() => {
          setOpen(!selectDisabled && !open);
        }}
        onKeyDown={(event) => {
          if (
            // Key was a letter other than space
            (event.key.match(/^\p{L}$/u /* Unicode Letter */) && event.key !== ' ') ||
            // Or space while searching. Otherwise space will select the option.
            (event.key === ' ' && searchTimeout !== 0)
          ) {
            const letter = event.key.toLowerCase();
            const mappedOptions = (open ? sortedOptions : options).map((option) =>
              typeof option.title === 'string'
                ? option.title.toLowerCase()
                : option.value.toString().toLowerCase(),
            );
            const searchString = getSearchString(letter);
            const searchIndex = getIndexByLetter(
              mappedOptions,
              searchString,
              (activeOptionIndex ?? -1) + 1,
            );
            if (searchIndex !== -1) {
              setActiveOptionIndex(searchIndex);

              // If still closed, select option immediately
              if (!open) {
                const searchedOption = options.at(searchIndex)!;
                setSelectedValue(searchedOption.value);
                onChange?.(searchedOption.value, event);
              }
            }
          } else if (event.key === 'Escape' || event.key === 'Tab') {
            setOpen(false);
          } else if (event.key === 'Enter' || event.key === ' ' /* Space */) {
            event.preventDefault(); // Space will push down the viewport otherwise
            if (!open) {
              setOpen(true);
              setActiveOptionIndex(0); // We sort the selected option to the beginning
            } else if (activeOptionIndex !== undefined) {
              setOpen(false);
              const activeOption = sortedOptions.at(activeOptionIndex);
              if (activeOption) {
                setSelectedValue(activeOption?.value);
                onChange?.(activeOption?.value, event);
              }
            }
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setActiveOptionIndex((prevIndex) => {
              const nextIndex = prevIndex !== undefined ? prevIndex - 1 : 0;
              return nextIndex >= 0 ? nextIndex : 0;
            });
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveOptionIndex((prevIndex) => {
              const nextIndex = prevIndex !== undefined ? prevIndex + 1 : 0;
              return nextIndex < options.length ? nextIndex : options.length - 1;
            });
          } else if (event.key === 'PageUp') {
            setActiveOptionIndex((prevIndex) => {
              const nextIndex = prevIndex !== undefined ? prevIndex - 10 : 0;
              return nextIndex >= 0 ? nextIndex : 0;
            });
          } else if (event.key === 'PageDown') {
            setActiveOptionIndex((prevIndex) => {
              const nextIndex = prevIndex !== undefined ? prevIndex + 10 : 0;
              return nextIndex < options.length ? nextIndex : options.length - 1;
            });
          } else if (event.key === 'Home') {
            setActiveOptionIndex(0);
          } else if (event.key === 'End') {
            setActiveOptionIndex(options.length - 1);
          }
        }}
      >
        {renderStyledDropdownOpener(selectedOption, caretSize)}
        {open && options.length > 1 && (
          <div id={listboxId} role="listbox" aria-labelledby={labelledById} tabIndex={-1}>
            <StyledOptionsWrapper>
              {sortedOptions.map((option, index) =>
                renderStyledDropdownOption(option, index, caretSize),
              )}
            </StyledOptionsWrapper>
          </div>
        )}
      </StyledDropdownSelectWrapper>
    </>
  );
}

const DropdownSelect = forwardRef(DropdownSelectComponent);
export default DropdownSelect;
